import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { InView } from 'react-intersection-observer';
import { promoShape } from '@bibliocommons/bc-prop-types';

import cardShape from 'app/shapes/cardShape';
import EventDateTime from 'app/components/shared/EventDateTime';
import AddToInterestedEvents from 'app/components/events/AddToInterestedEvents';
import { EVENT_TYPE } from 'app/constants/EventsConstants';
import ContentCardType from './ContentCardType';
import ContentCardImage from './ContentCardImage';
import CreativeCommons from './CreativeCommons';
import ContentCardTitle from './ContentCardTitle';
import ContentCardDescription from './ContentCardDescription';
import DebugCardButton from './DebugCardButton';
import EventDateStamp from './EventDateStamp';
import EventLocation from './EventLocation';

import './ContentCard.scss';

export default function ContentCard({
  className,
  card,
  compact,
  debugEnabled,
  headingTag,
  promo,
  cmsUrl,
  isInterestedEventsEnabled,
  interestedEvents,
  handleTitleClick,
  handleImageClick,
  handleContentImpression,
  handleContentExplore,
  handleCardEventInterestedClick,
  trackEventLinkClick,
  hasBackground,
  hasBorder,
  hasDescription,
  hasType,
  dateStampSize,
  trackEventLocationClick,
  renderBadge,
  dateLink,
  hasTitle
}) {
  const attribution = card.getIn(['image', 'bwCardImage11', 'attribution']);
  const eventStart = card.get('event') && card.getIn(['event', 'startTime']);
  const eventEnd = card.get('event') && card.getIn(['event', 'endTime']);
  const classNames = cn('cp-content-card', className, {
    'content-card--compact': compact,
    'content-card--borderless': !hasBorder,
    'content-card--transparent': !hasBackground
  });
  const isIncludeDescription = hasDescription && !compact;

  const manualDate = card?.getIn(['event', 'date']);
  const isShowManualDate = !(eventStart || eventEnd) && Boolean(manualDate);

  const savedEvent =
    card?.get('event') &&
    interestedEvents?.find(item => item?.get('eventIds')?.includes(card.getIn(['event', 'eventId'])));

  function handleVisibility(isVisible) {
    if (isVisible) {
      handleContentImpression(card);
    }
  }

  return (
    <InView className={classNames} threshold={1} onChange={handleVisibility} triggerOnce>
      {card.getIn(['event', 'startTime']) ? (
        <EventDateStamp card={card} compact={compact} size={dateStampSize} />
      ) : (
        <div className="image-wrapper">
          <ContentCardImage card={card} compact={compact} handleClick={handleImageClick} />
          {attribution ? <CreativeCommons attribution={attribution} /> : null}
        </div>
      )}
      <div className="card-details">
        {hasType && (
          <ContentCardType
            card={card}
            recommendationType={promo?.get('recommendationType')}
            cmsUrl={cmsUrl}
            handleContentExplore={handleContentExplore}
          />
        )}
        {renderBadge()}
        <ContentCardTitle card={card} tagName={headingTag} handleClick={handleTitleClick} hasTitle={hasTitle} />
        {isIncludeDescription && <ContentCardDescription card={card} />}
        {card.get('event') && (
          <EventDateTime
            start={eventStart}
            end={eventEnd}
            dateLink={dateLink}
            trackEventLinkClick={trackEventLinkClick}
          />
        )}
        {isShowManualDate && <p className="manual-date">{manualDate}</p>}
        <EventLocation card={card} trackClick={trackEventLocationClick} />
        {debugEnabled && <DebugCardButton card={card} promo={promo} />}
      </div>
      {isInterestedEventsEnabled &&
        card.getIn(['event', 'eventId']) &&
        card.getIn(['event', 'type']) === EVENT_TYPE.EVENT_BIBLIOEVENTS && (
          <div className="card-actions">
            <AddToInterestedEvents
              eventId={card.getIn(['event', 'eventId'])}
              seriesId={card.getIn(['event', 'seriesId'])}
              savedEvent={savedEvent}
              promos
              card={card}
              handleCardEventInterestedClick={handleCardEventInterestedClick}
            />
          </div>
        )}
    </InView>
  );
}

ContentCard.defaultProps = {
  handleContentImpression: () => null,
  hasBackground: true,
  hasDescription: true,
  hasBorder: true,
  hasType: true,
  hasTitle: true,
  dateLink: '',
  renderBadge: () => null,
  trackEventLocationClick: () => null,
  trackEventLinkClick: () => null
};

ContentCard.propTypes = {
  className: PropTypes.string,
  card: cardShape.isRequired,
  compact: PropTypes.bool,
  headingTag: PropTypes.string,
  promo: promoShape,
  debugEnabled: PropTypes.bool,
  cmsUrl: PropTypes.string,
  hasBackground: PropTypes.bool,
  hasBorder: PropTypes.bool,
  hasDescription: PropTypes.bool,
  hasType: PropTypes.bool,
  dateStampSize: PropTypes.oneOf(['default', 'large']),
  isInterestedEventsEnabled: PropTypes.bool,
  interestedEvents: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      registrationId: PropTypes.string.isRequired,
      eventIds: PropTypes.object.isRequired
    })
  ),
  handleTitleClick: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleContentImpression: PropTypes.func,
  handleContentExplore: PropTypes.func,
  handleCardEventInterestedClick: PropTypes.func,
  trackEventLinkClick: PropTypes.func,
  trackEventLocationClick: PropTypes.func,
  renderBadge: PropTypes.func,
  dateLink: PropTypes.string,
  hasTitle: PropTypes.bool
};
